// =============================================================================
// GOUVERNANCE - Audrey : adresse publique de la voix (url_publique_voix)
// =============================================================================
// Réglage de plateforme, non secret, lu par Audrey v2 seulement : Twilio signe
// l'adresse publique qu'il appelle, et Audrey lui rend des adresses de rappel
// bâties dessus. Écriture réservée au super admin (le serveur le refuse sinon).
// =============================================================================

import { useCallback, useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const URL_REGLAGE = `${API}/api/voice2/admin/url-publique`;

const carte = {
  background: "var(--oh-bg-elevated)",
  border: "0.5px solid var(--oh-border)",
  borderRadius: 12,
  padding: 16,
};

async function appeler(methode, corps) {
  const reponse = await fetch(URL_REGLAGE, {
    method: methode,
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-OmniHub-CSRF": "1" },
    body: corps === undefined ? undefined : JSON.stringify(corps),
  });
  let donnees = null;
  try {
    donnees = await reponse.json();
  } catch {
    donnees = null;
  }
  if (!reponse.ok) {
    const detail = donnees && typeof donnees.detail === "string" ? donnees.detail : `Erreur ${reponse.status}`;
    throw new Error(detail);
  }
  return donnees;
}

export default function GouvAudrey({ currentUser }) {
  const estSuperAdmin = currentUser?.rôle === "super_admin" || !!currentUser?.is_super_admin;
  const [etat, setEtat] = useState(null);
  const [saisie, setSaisie] = useState("");
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");

  const appliquer = useCallback((donnees) => {
    setEtat(donnees);
    setSaisie(donnees.personnalisee ? donnees.url_publique_voix : "");
  }, []);

  useEffect(() => {
    if (!estSuperAdmin) {
      setChargement(false);
      return undefined;
    }
    let actif = true;
    appeler("GET")
      .then((d) => actif && appliquer(d))
      .catch((e) => actif && setErreur(e.message))
      .finally(() => actif && setChargement(false));
    return () => {
      actif = false;
    };
  }, [estSuperAdmin, appliquer]);

  const enregistrer = async (valeur) => {
    setEnCours(true);
    setErreur("");
    setMessage("");
    try {
      appliquer(await appeler("PUT", { url_publique_voix: valeur }));
      setMessage(valeur ? "Adresse enregistrée." : "Adresse par défaut rétablie.");
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnCours(false);
    }
  };

  if (!estSuperAdmin) {
    return (
      <div className="p-3 md:p-6" data-testid="gouv-audrey">
        <div style={{ ...carte, color: "var(--oh-text-secondary)" }} data-testid="gouv-audrey-refus">
          Ce réglage est réservé au super admin.
        </div>
      </div>
    );
  }

  const valeurActive = etat ? etat.url_publique_voix : "";

  return (
    <div className="space-y-4 max-w-2xl" data-testid="gouv-audrey">
      <div>
        <h2 style={{ color: "var(--oh-text)", fontSize: 18, fontWeight: 500, margin: 0 }}>Audrey</h2>
        <p style={{ color: "var(--oh-text-secondary)", fontSize: 14, lineHeight: 1.6, margin: "4px 0 0" }}>
          Adresse publique de la voix. Twilio signe cette adresse, et Audrey s'en sert pour toutes ses adresses de rappel.
        </p>
      </div>

      <div style={carte}>
        <label
          htmlFor="gouv-audrey-url"
          style={{ display: "block", color: "var(--oh-text)", fontSize: 14, fontWeight: 500, marginBottom: 8 }}
        >
          Adresse publique de la voix
        </label>
        <input
          id="gouv-audrey-url"
          type="url"
          value={saisie}
          disabled={chargement || enCours}
          placeholder={etat ? etat.defaut : "https://omnihub-ia.fr"}
          onChange={(e) => setSaisie(e.target.value)}
          data-testid="gouv-audrey-url"
          style={{
            width: "100%",
            height: 36,
            borderRadius: 8,
            padding: "0 12px",
            background: "var(--oh-bg-surface)",
            color: "var(--oh-text)",
            border: "0.5px solid var(--oh-border)",
          }}
        />
        <p style={{ color: "var(--oh-text-muted)", fontSize: 12, lineHeight: 1.6, margin: "8px 0 0" }}>
          Adresse du serveur seule, en https, sans chemin. Vide : adresse par défaut{etat ? ` (${etat.defaut})` : ""}.
        </p>

        {!chargement && etat && (
          <div
            data-testid="gouv-audrey-active"
            style={{
              marginTop: 12,
              padding: "8px 12px",
              borderRadius: 8,
              background: "var(--oh-info-bg)",
              color: "var(--oh-info-text)",
              border: "0.5px solid var(--oh-info-border)",
              fontSize: 14,
              wordBreak: "break-all",
            }}
          >
            Utilisée par Audrey : {valeurActive} {etat.personnalisee ? "(personnalisée)" : "(par défaut)"}
          </div>
        )}

        {erreur && (
          <div
            role="alert"
            data-testid="gouv-audrey-erreur"
            style={{
              marginTop: 12,
              padding: "8px 12px",
              borderRadius: 8,
              background: "var(--oh-error-bg)",
              color: "var(--oh-error-text)",
              border: "0.5px solid var(--oh-error-border)",
              fontSize: 14,
            }}
          >
            {erreur}
          </div>
        )}
        {message && !erreur && (
          <div
            role="status"
            data-testid="gouv-audrey-message"
            style={{
              marginTop: 12,
              padding: "8px 12px",
              borderRadius: 8,
              background: "var(--oh-success-bg)",
              color: "var(--oh-success-text)",
              border: "0.5px solid var(--oh-success-border)",
              fontSize: 14,
            }}
          >
            {message}
          </div>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={() => enregistrer(saisie.trim())}
            disabled={chargement || enCours || !saisie.trim()}
            data-testid="gouv-audrey-enregistrer"
            style={{
              height: 36,
              padding: "0 16px",
              borderRadius: 8,
              border: "none",
              fontWeight: 500,
              background: "var(--oh-accent-solid)",
              color: "var(--oh-accent-contrast)",
              opacity: chargement || enCours || !saisie.trim() ? 0.5 : 1,
            }}
          >
            Enregistrer l'adresse
          </button>
          <button
            type="button"
            onClick={() => enregistrer(null)}
            disabled={chargement || enCours || !(etat && etat.personnalisee)}
            data-testid="gouv-audrey-defaut"
            style={{
              height: 36,
              padding: "0 16px",
              borderRadius: 8,
              background: "transparent",
              color: "var(--oh-text-secondary)",
              border: "0.5px solid var(--oh-border)",
              opacity: chargement || enCours || !(etat && etat.personnalisee) ? 0.5 : 1,
            }}
          >
            Rétablir l'adresse par défaut
          </button>
        </div>
      </div>

      <p style={{ color: "var(--oh-text-muted)", fontSize: 12, lineHeight: 1.6, margin: 0 }}>
        L'adresse des webhooks dans la console Twilio doit être exactement celle-ci, suivie de /api/voice2/...
      </p>
    </div>
  );
}
